import React from 'react';
import { useCrossmintEvents } from '@crossmint/client-sdk-react-ui';
import { LoadingBars } from '@components/LoadingBars/LoadingBars';
import { Header, Modal } from '@components/main-components/modals/Modal';
import { useMintReferral } from '@context/mintReferral';
import { ModalContext } from '@context/modal';
import { Transaction } from '@context/transaction';
import { useLogError } from '@hooks/useLogErrorNew';
import { CROSSMINT_ENVIRONMENT } from '@utils/constants';
import { assertIsHex } from '@utils/hex';
import { sendReferralSourceIfPresent } from './Buy/ReferralSource';

const dedupeStore = new Set<string>();

export const CrossMintProcessingModal = ({
  orderIdentifier,
  releaseId,
  chainId,
}: {
  orderIdentifier: string;
  releaseId: string;
  chainId: number;
}) => {
  const { closeCancelModal } = ModalContext.useContainer();
  const logError = useLogError();
  const { safeHandleTransaction } = Transaction.useContainer();
  const { listenToMintingEvents } = useCrossmintEvents({
    environment: CROSSMINT_ENVIRONMENT,
  });
  const { referral } = useMintReferral();

  listenToMintingEvents({ orderIdentifier }, async event => {
    switch (event.type) {
      case 'transaction:fulfillment.succeeded': {
        const { txId } = event.payload;
        if (txId) {
          const key = `${txId}:${chainId}`;
          /**
           * It seems like the crossmint sdk is emitting the same event multiple times
           * so we need to dedupe the txId
           */
          if (dedupeStore.has(key)) return;
          dedupeStore.add(key);

          assertIsHex(txId);

          sendReferralSourceIfPresent({
            txHash: txId,
            chainId,
            referral,
          });

          await safeHandleTransaction({
            transactionFn: async () => txId,
            contractMethod: 'SAM__BUY_EDITION',
            releaseId,
            showOpenWalletToast: false,
            chainId,
            openProcessingModal: false,
          });
        }
        break;
      }
      case 'payment:process.canceled':
      case 'payment:process.rejected':
      case 'payment:preparation.failed':
      case 'transaction:fulfillment.failed': {
        const key = event.payload.error.code;
        /**
         * It seems like the crossmint sdk is emitting the same event multiple times
         * so we need to dedupe the txId
         */
        if (dedupeStore.has(key)) return;
        dedupeStore.add(key);
        logError({
          level: 'error',
          errorType: 'CROSSMINT_CC',
          action: 'CROSSMINT_ERROR',
          isArtistOnlyPage: false,
          error: new Error('CrossMint transaction failed'),
          message: 'CrossMint transaction failed',
          pillar: 'MINTING',
          indexedTags: {
            orderIdentifier,
            chainId,
            releaseId,
          },
          unindexedExtra: {
            event,
          },
        });
        closeCancelModal();
        break;
      }
      default: {
        break;
      }
    }
  });

  const body = (
    <>
      <div className="mb-2 flex w-full justify-center">
        <LoadingBars height={undefined} />
      </div>
      <Header css={{ justifyContent: 'center', marginTop: 8, '@tablet': { marginTop: 16 } }}>
        Processing...
      </Header>
      <div className="mt-2 text-center text-base-m md:mt-4 md:text-base-l">
        <p className="font-normal leading-5 text-neutral700">Transaction initiated!</p>
      </div>
    </>
  );

  return (
    <Modal
      body={body}
      closeModal={closeCancelModal}
      headerStyles={{ height: 0 }}
      bodyStyles={{ marginTop: 0 }}
    />
  );
};
